import {getTestProps} from '@benchling/js-test-utils';
import {Badge, createStyles} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React from 'react';

import TextFilePreviewModal from 'files/preview/TextFilePreviewModal';
import type {InlineStructureTarget} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import type {BasicEvent} from 'util/Types';

const LABEL = 'Target Structure';

/**
 * A target structure given as PDB/mmCIF text rather than a Benchling File. It has no file to open or
 * download, so clicking the chip only previews the structure.
 */
export default function StructureTextInputChip({
  structure,
  iconOnly = false,
}: {
  structure: InlineStructureTarget;
  iconOnly?: boolean;
}): JSX.Element {
  const {classes} = useStyles();
  const [opened, {open, close}] = useDisclosure(false);

  return (
    <>
      <Badge
        aria-label={iconOnly ? LABEL : undefined}
        className={classes.root}
        color="gray"
        component="button"
        leftSection={<BenchlingIcon aria-hidden={true} icon="file" />}
        onClick={(event: BasicEvent) => {
          event.stopPropagation();
          open();
        }}
        radius="sm"
        size="sm"
        tt="none"
        type="button"
        variant="light"
        {...getTestProps({component: 'StructureTextInputChip', element: 'chip'})}
      >
        {!iconOnly && LABEL}
      </Badge>
      <span
        onClick={(event: BasicEvent) => {
          event.stopPropagation();
        }}
      >
        <TextFilePreviewModal
          filename={`target.${structure.format}`}
          onClose={close}
          opened={opened}
          text={structure.text}
          title={`${LABEL} (.${structure.format})`}
        />
      </span>
    </>
  );
}

// The Nucleus Badge theme pins `root` to a fixed height with `overflow: hidden`, which crops descenders.
const useStyles = createStyles(() => ({
  root: {
    cursor: 'pointer',
    height: 'auto',
    minHeight: 18,
  },
}));
